import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { accountingApi, type AccountingBankMovementRecord } from '@/lib/api/accounting';
import { readRegisteredCheques } from '@/lib/chequeBooksStorage';

type ReminderItem = {
  id: string;
  date: string;
  amount: number;
  ref: string;
  description: string;
};

function toFaDigits(value: string | number): string {
  return String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
}

function toAsciiDigits(value: string): string {
  return value.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
}

function normalizeDateComparable(value: string): string {
  return toAsciiDigits(value || '').replace(/\D/g, '');
}

function todayFaDate(): string {
  const formatted = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return formatted.replace(/[^\d/۰-۹]/g, '');
}

const LoginReminderModal: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const [loading, setLoading] = React.useState(false);
  const [dueSoon, setDueSoon] = React.useState<ReminderItem[]>([]);
  const [overdue, setOverdue] = React.useState<ReminderItem[]>([]);

  React.useEffect(() => {
    if (!open) return;
    setLoading(true);
    const todayComparable = normalizeDateComparable(todayFaDate());
    accountingApi
      .bankMovements()
      .then((items) => {
        const chequeItems = (items || []).filter(
          (x) => x.paymentMethod === 'cheque' && (x.valueDate || '').trim() !== '',
        );
        const mapped: ReminderItem[] = chequeItems.map((x: AccountingBankMovementRecord) => ({
          id: x.id,
          date: x.valueDate || '',
          amount: Number(x.amount || 0),
          ref: x.referenceNo || '-',
          description: x.description || '-',
        }));

        const registeredChequeMapped: ReminderItem[] = readRegisteredCheques()
          .filter((x) => x.status === 'pending' && (x.dueDate || '').trim() !== '')
          .map((x) => {
            const leafFa = toFaDigits(x.leafNumber);
            const refParts = [x.serie ? `سری ${x.serie}` : '', x.leafNumber ? `برگ ${leafFa}` : '', x.nationalCode].filter(
              Boolean,
            );
            return {
              id: `reg-${x.id}`,
              date: x.dueDate,
              amount: Number(x.amount || 0),
              ref: refParts.join(' · ') || '-',
              description: `${x.fullName}${x.bankAccountLabel ? ` — ${x.bankAccountLabel}` : ''}${x.description ? ` — ${x.description}` : ''}`,
            };
          });

        const allItems = [...mapped, ...registeredChequeMapped];

        const overdueRows = allItems
          .filter((x) => normalizeDateComparable(x.date) < todayComparable)
          .sort((a, b) => normalizeDateComparable(a.date).localeCompare(normalizeDateComparable(b.date)));
        const dueSoonRows = allItems
          .filter((x) => normalizeDateComparable(x.date) >= todayComparable)
          .sort((a, b) => normalizeDateComparable(a.date).localeCompare(normalizeDateComparable(b.date)));

        setOverdue(overdueRows.slice(0, 8));
        setDueSoon(dueSoonRows.slice(0, 8));
      })
      .catch(() => {
        setOverdue([]);
        setDueSoon([]);
      })
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto [&>button]:left-4 [&>button]:right-auto">
        <DialogHeader>
          <DialogTitle>یادآور مالی (بدهی‌ها / سررسید چک‌ها)</DialogTitle>
        </DialogHeader>
        {loading ? <div className="text-sm text-slate-500">در حال بارگذاری یادآورها...</div> : null}

        {!loading && overdue.length === 0 && dueSoon.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
            موردی برای یادآوری ثبت نشده است.
          </div>
        ) : null}

        {!loading && overdue.length > 0 ? (
          <div className="space-y-2">
            <div className="text-sm font-semibold text-rose-700">چک‌های معوق (سررسید گذشته)</div>
            <div className="overflow-x-auto rounded-md border border-rose-200">
              <table className="w-full min-w-[620px] text-sm">
                <thead>
                  <tr className="bg-rose-50 text-rose-800">
                    <th className="border px-2 py-2 text-right">تاریخ</th>
                    <th className="border px-2 py-2 text-right">مبلغ</th>
                    <th className="border px-2 py-2 text-right">مرجع</th>
                    <th className="border px-2 py-2 text-right">شرح</th>
                  </tr>
                </thead>
                <tbody>
                  {overdue.map((x) => (
                    <tr key={x.id}>
                      <td className="border px-2 py-2">{x.date}</td>
                      <td className="border px-2 py-2 font-mono">{x.amount.toLocaleString('en-US')}</td>
                      <td className="border px-2 py-2">{x.ref}</td>
                      <td className="border px-2 py-2">{x.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {!loading && dueSoon.length > 0 ? (
          <div className="space-y-2">
            <div className="text-sm font-semibold text-amber-700">سررسید چک‌های پیش رو</div>
            <div className="overflow-x-auto rounded-md border border-amber-200">
              <table className="w-full min-w-[620px] text-sm">
                <thead>
                  <tr className="bg-amber-50 text-amber-800">
                    <th className="border px-2 py-2 text-right">تاریخ</th>
                    <th className="border px-2 py-2 text-right">مبلغ</th>
                    <th className="border px-2 py-2 text-right">مرجع</th>
                    <th className="border px-2 py-2 text-right">شرح</th>
                  </tr>
                </thead>
                <tbody>
                  {dueSoon.map((x) => (
                    <tr key={x.id}>
                      <td className="border px-2 py-2">{x.date}</td>
                      <td className="border px-2 py-2 font-mono">{x.amount.toLocaleString('en-US')}</td>
                      <td className="border px-2 py-2">{x.ref}</td>
                      <td className="border px-2 py-2">{x.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default LoginReminderModal;
